// The composer's + menu: "Upload a file" (on phones three big tiles, Photos, Camera and File, like Discord's: one tap
// to the phone's photo picker), "Pin or add to a collection", then every app in the server with its commands, read from the same command
// registry as the "/" menu (and @Bloom's tools), filtered to what I can run. Picking a command runs it exactly like the
// "/" menu does with Tab: its form when it takes input, straight away when it doesn't. A floating menu on desktop, a
// bottom sheet on phones. Like every menu, it ends with "Bloom a new app" and "Make a new bot" (where blooming is allowed).
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { commandRegistry, type AppManifest, type CommandEntry, type Role } from "@bloom/shared";
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";

/** Where an upload comes from: the photo picker (pictures and videos), the camera, or any file. */
export type UploadSource = "photos" | "camera" | "files";

/**
 * Opens the composer's one file input as the picker a source asks for, from the tap itself (browsers open a file
 * picker only from a person's own tap): Photos asks for pictures and videos only, which opens the phone's photo picker
 * (Android; iPhone Safari shows its short menu first), Camera adds `capture` (straight to the camera), File takes any
 * file. The input keeps the last setting, so every other way in (+ with no menu, the desktop row) asks for "files".
 */
export function openPicker(input: HTMLInputElement | null, source: UploadSource) {
  if (!input) return;
  if (source === "files") input.removeAttribute("accept");
  else input.accept = source === "photos" ? "image/*,video/*" : "image/*";
  if (source === "camera") input.setAttribute("capture", "environment");
  else input.removeAttribute("capture");
  input.multiple = source !== "camera";
  input.click();
}

type Row = { kind: "upload" } | { kind: "collections" } | { kind: "app"; app: AppManifest } | { kind: "command"; entry: CommandEntry } | { kind: "bloom"; bot: boolean };

export function PlusMenu({ apps, role, onUpload, onPick, onOpenApp, onClose, onBloom, collections }: {
  apps: AppManifest[];
  /** "Pin or add to a collection" (applied when the message is sent): how many are picked, and opening the picker. */
  collections?: { picked: number; open: () => void };
  role?: Role;
  /** "Bloom a new app" or "Make a new bot" (starts a blooming thread); omitted where it isn't offered. */
  onBloom?: (kind: "app" | "bot") => void;
  onUpload: (source: UploadSource) => void;
  onPick: (entry: CommandEntry) => void;
  onOpenApp: (slug: string) => void;
  onClose: () => void;
}) {
  const sheet = isNarrow();
  const box = useRef<HTMLDivElement>(null);
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [{ kind: "upload" }];
    if (collections) out.push({ kind: "collections" });
    const reg = commandRegistry(apps, role);
    for (const app of apps) {
      out.push({ kind: "app", app });
      for (const entry of reg) if (entry.app === app && entry.command) out.push({ kind: "command", entry });
    }
    if (onBloom) out.push({ kind: "bloom", bot: false }, { kind: "bloom", bot: true });
    return out;
  }, [apps, role, !!onBloom, !!collections]);
  const [index, setIndex] = useState(0);

  const pick = (r: Row) => {
    onClose();
    if (r.kind === "upload") onUpload("files");
    else if (r.kind === "collections") collections?.open();
    else if (r.kind === "app") onOpenApp(r.app.slug);
    else if (r.kind === "bloom") onBloom?.(r.bot ? "bot" : "app");
    else onPick(r.entry);
  };

  useEffect(() => {
    if (sheet) return;
    box.current?.focus({ preventScroll: true });
    const outside = (e: PointerEvent) => {
      const t = e.target as Element;
      if (!box.current?.contains(t) && !t.closest(".plus-btn")) onClose();
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [sheet]);

  useEffect(() => {
    box.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
  }, [index]);

  const list = (
    <div
      ref={box}
      className={`plus-menu${sheet ? " in-sheet" : ""}`}
      role="menu"
      aria-label="Add to your message"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          setIndex((i) => (i + (e.key === "ArrowDown" ? 1 : rows.length - 1)) % rows.length);
        } else if (e.key === "Enter") {
          e.preventDefault();
          pick(rows[index]!);
        } else if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
      }}
    >
      {rows.map((r, i) => {
        const key = r.kind === "upload" || r.kind === "collections" ? r.kind : r.kind === "bloom" ? (r.bot ? "bloom-bot" : "bloom") : r.kind === "app" ? `app:${r.app.slug}` : r.entry.label;
        const common = {
          type: "button" as const,
          role: "menuitem",
          "data-index": i,
          onMouseEnter: () => setIndex(i),
          onClick: () => pick(r),
        };
        if (r.kind === "upload" && sheet) {
          // Phones: Photos, Camera and File as three big tiles. Each opens its picker from this tap (browsers open a
          // file picker only from a person's own tap), so the sheet closes first and the picker opens straight after.
          const tile = (source: UploadSource, label: string, icon: React.ReactNode) => (
            <button
              type="button"
              role="menuitem"
              className="plus-tile"
              data-source={source}
              onClick={() => {
                onClose();
                onUpload(source);
              }}
            >
              <span className="plus-tile-glyph" aria-hidden="true">{icon}</span>
              <span className="plus-tile-label">{label}</span>
            </button>
          );
          return (
            <div key={key} className="plus-tiles" role="group" aria-label="Upload" data-index={i}>
              {tile("photos", "Photos", <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="10" r="2" /><path d="m21 16-5-5-9 9" /></svg>)}
              {tile("camera", "Camera", <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" /><circle cx="12" cy="13" r="3.5" /></svg>)}
              {tile("files", "File", <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>)}
            </div>
          );
        }
        if (r.kind === "upload") {
          return (
            <button key={key} {...common} className={`plus-item${i === index && !sheet ? " active" : ""}`}>
              <span className="plus-glyph" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V4M6 10l6-6 6 6M4 20h16" /></svg>
              </span>
              <span className="plus-text">
                <span className="plus-label">Upload a file</span>
                <span className="plus-desc">Photos, videos and files, up to 10 at a time</span>
              </span>
            </button>
          );
        }
        if (r.kind === "collections") {
          const n = collections?.picked ?? 0;
          return (
            <button key={key} {...common} className={`plus-item${i === index && !sheet ? " active" : ""}`}>
              <span className="plus-glyph" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 13.5V21l-5-3.5L7 21V5a2 2 0 0 1 2-2h4" /><path d="M18 3v6M15 6h6" /></svg>
              </span>
              <span className="plus-text">
                <span className="plus-label">{n ? `Pin or add to a collection (${n} picked)` : "Pin or add to a collection"}</span>
                <span className="plus-desc">When the message is sent. Or type +name</span>
              </span>
            </button>
          );
        }
        // Groups (each app, then "Bloom a new app" with "Make a new bot") are split by a plain rule with the same space above and below.
        const sep = <div className="plus-sep" role="separator" />;
        if (r.kind === "bloom") {
          return (
            <Fragment key={key}>
              {!r.bot && sep}
              <button {...common} className={`plus-item plus-bloom${i === index && !sheet ? " active" : ""}`}>
                <span className="plus-glyph app-glyph" aria-hidden="true">{r.bot ? "🤖" : "🌱"}</span>
                <span className="plus-text">
                  <span className="plus-label">{r.bot ? "Make a new bot" : "Bloom a new app"}</span>
                  <span className="plus-desc">{r.bot ? "Your own bot that lives in chat, built with Bloom in private" : "Describe an app and build it with Bloom in a thread"}</span>
                </span>
              </button>
            </Fragment>
          );
        }
        if (r.kind === "app") {
          return (
            <Fragment key={key}>
              {sep}
              <button {...common} className={`plus-app${i === index && !sheet ? " active" : ""}`} title={`Open ${r.app.name}`}>
                <span className="plus-app-name">{r.app.name}</span>
                <span className="plus-app-open">Open</span>
              </button>
            </Fragment>
          );
        }
        const { entry } = r;
        return (
          <button key={key} {...common} className={`plus-item${i === index && !sheet ? " active" : ""}`} data-command={entry.label}>
            <span className="plus-glyph app-glyph" aria-hidden="true">{entry.app.icon}</span>
            <span className="plus-text">
              <span className="plus-label">{entry.label}</span>
              <span className="plus-desc">{entry.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
  return sheet ? <Sheet label="Add to your message" onClose={onClose}>{list}</Sheet> : list;
}
